import React, { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Typography,
    alpha,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ENDPOINT } from "apiRoutes";
import { getEnvVar } from "envConfig";
import { useOrderStatus } from "hooks/useOrderStatus";
import { enableOrderPush } from "services/OrderTrackingService";

const STATUS_LABELS = {
    pending: "Pending",
    confirmed: "Confirmed",
    preparing: "Preparing",
    ready: "Ready",
    out_for_delivery: "Out for delivery",
    completed: "Completed",
    cancelled: "Cancelled",
};

const OrderTracking = () => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get("companyId");
    const orderNoFromQuery = searchParams.get("orderNo");

    const [enablingPush, setEnablingPush] = useState(false);
    const [pushEnabled, setPushEnabled] = useState(false);
    const [pushHint, setPushHint] = useState("");

    const { status, orderNumber, connected, loading, error } = useOrderStatus({
        baseUrl: ENDPOINT,
        companyId,
        orderId,
    });

    const vapidPublicKey = useMemo(
        () =>
            getEnvVar("VAPID_PUBLIC_KEY") ||
            process.env.REACT_APP_VAPID_PUBLIC_KEY ||
            process.env.VITE_VAPID_PUBLIC_KEY ||
            window.REACT_APP_VAPID_PUBLIC_KEY ||
            "",
        [],
    );

    const statusText = STATUS_LABELS[status] || status || "Waiting for status";
    const displayOrderNumber = orderNoFromQuery || orderNumber || "Pending";

    const handleEnableNotifications = async () => {
        try {
            setEnablingPush(true);
            setPushHint("");

            await enableOrderPush({
                baseUrl: ENDPOINT,
                companyId,
                orderId,
                vapidPublicKey,
            });

            setPushEnabled(true);
            toast.success("Order notifications enabled");
        } catch (err) {
            const message = err.message || "Unable to enable notifications";
            setPushHint(message);
            toast.error(message);
        } finally {
            setEnablingPush(false);
        }
    };

    if (!orderId || !companyId) {
        return (
            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Alert severity="error">
                    Missing order details. Please open a valid tracking link with orderId and companyId.
                </Alert>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: { xs: 4, md: 8 },
                background:
                    "radial-gradient(1200px 520px at 100% -10%, rgba(25,118,210,0.18), transparent 60%), radial-gradient(900px 420px at 0% 110%, rgba(46,125,50,0.14), transparent 60%), #f6f8fb",
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 4,
                        overflow: "hidden",
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        boxShadow: "0 12px 35px rgba(15, 23, 42, 0.08)",
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2.5, sm: 3.5 },
                            py: { xs: 2.5, sm: 3 },
                            color: "#fff",
                            background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 45%, #1e88e5 100%)",
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1.1 }}>
                                    Live Tracking
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                    Order #{displayOrderNumber}
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                icon={connected ? <WifiRoundedIcon /> : <WifiOffRoundedIcon />}
                                label={connected ? "Live" : "Reconnecting"}
                                sx={{
                                    bgcolor: connected ? "rgba(76, 175, 80, 0.2)" : "rgba(255,255,255,0.14)",
                                    color: "#fff",
                                    border: "1px solid rgba(255,255,255,0.3)",
                                }}
                            />
                        </Stack>
                    </Box>

                    <Stack spacing={2.2} sx={{ p: { xs: 2.2, sm: 3 } }}>
                        <Box
                            sx={{
                                borderRadius: 2.5,
                                p: 2,
                                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                bgcolor: "#ffffff",
                            }}
                        >
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.25 }}>
                                <LocalShippingRoundedIcon color="primary" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    Current Status
                                </Typography>
                            </Stack>
                            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                {loading ? "Loading..." : statusText}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                                We update this page automatically while it stays open.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                borderRadius: 2.5,
                                p: 2,
                                bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
                                border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                            }}
                        >
                            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                                <ReceiptLongRoundedIcon color="success" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    Notifications
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                Enable notifications to get updates even after closing this page.
                            </Typography>
                            <Button
                                variant="contained"
                                color={pushEnabled ? "success" : "primary"}
                                startIcon={
                                    enablingPush ? <CircularProgress color="inherit" size={16} /> : <NotificationsActiveRoundedIcon />
                                }
                                onClick={handleEnableNotifications}
                                disabled={enablingPush || pushEnabled}
                                sx={{
                                    fontWeight: 700,
                                    textTransform: "none",
                                    borderRadius: 2,
                                }}
                            >
                                {pushEnabled
                                    ? "Notifications enabled"
                                    : enablingPush
                                        ? "Enabling notifications..."
                                        : "Enable order notifications"}
                            </Button>
                            {!vapidPublicKey && !pushEnabled && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                    Notification setup is partially configured. Contact support if enabling fails.
                                </Typography>
                            )}
                        </Box>

                        {pushHint && (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                {pushHint}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default OrderTracking;
